import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  CBadge,
  CButton,
  CCard,
  CCol,
  CFormLabel,
  CFormSelect,
  CLoadingButton,
  CMultiSelect,
  CRow,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from '@coreui/react-pro'
import { cilCheckCircle, cilLink, cilPlus, cilSync, cilWarning, cilX } from '@coreui/icons'
import ToastComponent from 'src/components/common/TaostComponent'
import {
  ImportErrorAlert,
  InfoBox,
  MiniStepper,
  StatTile,
} from 'src/components/common/ImportWizard'
import PlayHQService from 'src/service/PlayHQService'
import {
  ACTIONS,
  ActionSelect,
  BulkBar,
  CHECK_CELL_STYLE,
  BulkSelect,
  FilterPills,
  LinkedViewTabs,
  LoadingBlock,
  RecordSelect,
  RowCheckbox,
  SelectAllCheckbox,
  duplicateTargets,
  errorsOf,
  optionsOf,
  reportBulk,
  rowStyle,
  tableHeadStyle,
  useRowSelection,
} from './playhqShared'

const STEPS = ['Validate', 'Review & Import']

// A match already imported can only be updated from PlayHQ or left alone.
const allowedActions = (row) =>
  row.imported ? ['update', 'skip'] : ['create', 'link', 'update', 'skip']

// A league fixture is home team v away team; a club fixture is our team v an opponent's name.
export const fixtureLabel = (f) =>
  `${f.round_number ? `R${f.round_number} · ` : ''}${f.team_name || 'Team'} vs ${
    f.team2_name || f.opposition_club || 'TBC'
  } · ${f.start_date}${Number(f.match_type) === 28 ? ' · 2-Day' : ''}`

/**
 * Picks one of our fixtures, grouped by round. The round this PlayHQ match falls in (when the
 * game has one) is listed first, so the likely fixture is near the top.
 */
export const FixtureSelect = ({ value, onChange, fixtures, roundId, placeholder, invalid }) => {
  const groups = new Map()
  fixtures.forEach((f) => {
    const key = f.round_id && f.round_number ? String(f.round_id) : ''
    if (!groups.has(key)) groups.set(key, { number: f.round_number, fixtures: [] })
    groups.get(key).fixtures.push(f)
  })

  const ordered = [...groups.entries()].sort(([keyA, a], [keyB, b]) => {
    if (roundId && keyA === String(roundId)) return -1
    if (roundId && keyB === String(roundId)) return 1
    if (!keyA) return 1
    if (!keyB) return -1
    return Number(a.number) - Number(b.number)
  })

  return (
    <CFormSelect
      size="sm"
      value={value || ''}
      invalid={invalid}
      onChange={(e) => onChange(e.target.value)}
      style={{ minWidth: 240 }}
    >
      <option value="">{placeholder}</option>
      {ordered.map(([key, group]) => (
        <optgroup
          key={key || 'none'}
          label={
            key
              ? `Round ${group.number}${
                  roundId && key === String(roundId) ? ' - this match’s round' : ''
                }`
              : 'No round'
          }
        >
          {group.fixtures.map((f) => (
            <option key={f.id} value={f.id}>
              {fixtureLabel(f)}
            </option>
          ))}
        </optgroup>
      ))}
    </CFormSelect>
  )
}

// R1, R2 … R18, then finals in the order they are played, then anything else.
const FINALS = ['QF', 'EF', 'SF', 'PF', 'GF']
export const roundOrder = (name) => {
  const n = /^R(\d+)$/i.exec(name)
  if (n) return Number(n[1])
  const f = FINALS.indexOf(String(name).toUpperCase())
  return f >= 0 ? 1000 + f : 2000
}

// "IN_PROGRESS" → "In progress".
export const statusLabel = (v) => v.charAt(0) + v.slice(1).toLowerCase().replace(/_/g, ' ')

const NO_FILTERS = { teams: [], grade: '', round: '', ourRound: '', status: '' }

// What each filter compares. A league match is between two of the game's teams, so the team
// filter takes either side. `ourRound` is 'none' for a match no round of ours covers.
const filterValue = (league) => ({
  teams: (row) => (league ? [row.item.team_name, row.item.opponent] : [row.item.team_name]),
  grade: (row) => row.item.grade_name,
  round: (row) => row.item.round_short || row.item.round_name,
  ourRound: (row) => (row.item.round_number ? String(row.item.round_number) : 'none'),
  status: (row) => row.item.status,
})

const fitsFilters = (row, filters, valueOf) =>
  Object.entries(filters).every(([key, value]) => {
    if (Array.isArray(value))
      return !value.length || valueOf[key](row).some((v) => value.includes(v))
    return !value || valueOf[key](row) === value
  })

const sameValues = (a, b) => a.length === b.length && a.every((v) => b.includes(v))

const teamLabel = (t) => `${t.name}${t.grade ? ` (${t.grade})` : ''}`

export const STATUS_COLOR = {
  COMPLETED: 'success',
  ABANDONED: 'secondary',
  CANCELLED: 'secondary',
  IN_PROGRESS: 'info',
  UPCOMING: 'primary',
}

export const formatDate = (date, time) => {
  if (!date) return '—'
  const d = new Date(`${date}T00:00:00`)
  const day = Number.isNaN(d.getTime())
    ? date
    : d.toLocaleDateString(undefined, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
  return time ? `${day}, ${time}` : day
}

// One of our fixtures that is probably this match: same team (in a league, the same home and
// away teams), same start date, not yet linked.
export const sameDayFixture = (item, ourFixtures, teamId, team2Id = null) =>
  teamId
    ? ourFixtures.find(
        (f) =>
          !f.playhq_id &&
          String(f.team) === String(teamId) &&
          (!team2Id || String(f.team2) === String(team2Id)) &&
          f.start_date === item.start_date,
      )
    : null

/**
 * PlayHQ matches → our fixtures. Each match is created as a new fixture, linked to one the admin
 * already made (optionally taking PlayHQ's date, venue and opponent), or skipped. A fixture's
 * round comes from its start date, as it does when a fixture is added by hand, so a match that
 * no round covers cannot be created until the rounds are set up.
 *
 * In a league each match is one fixture between two of the game's teams: home and away.
 */
const FixturesImporter = ({ scope, teamIds, league = false, onImported }) => {
  const [data, setData] = useState(null)
  const [rows, setRows] = useState([])
  const [loadErrors, setLoadErrors] = useState([])
  const [step, setStep] = useState(1)
  const [filter, setFilter] = useState('all')
  const [view, setView] = useState('pending')
  const [filters, setFilters] = useState(NO_FILTERS)
  const [importing, setImporting] = useState(false)
  const [importErrors, setImportErrors] = useState([])
  const [result, setResult] = useState(null)
  const selection = useRowSelection()
  const clearSelection = selection.clear

  const load = useCallback(() => {
    setData(null)
    setLoadErrors([])
    setResult(null)
    setImportErrors([])
    setStep(1)
    clearSelection()
    PlayHQService.preview('fixtures', { scope, team_ids: teamIds })
      .then((res) => {
        if (!res.success) {
          setLoadErrors(errorsOf(res))
          return
        }
        setData(res.data)
        setRows(
          (res.data.items || []).map((item) => {
            const teamId = item.team_id ? String(item.team_id) : ''
            const team2Id = item.team2_id ? String(item.team2_id) : ''
            if (item.fixture_id) {
              return {
                key: item.match_id,
                item,
                action: 'skip',
                team_id: teamId,
                team2_id: team2Id,
                target_id: String(item.fixture_id),
                imported: true,
              }
            }
            const candidate = sameDayFixture(
              item,
              res.data.our_fixtures || [],
              teamId,
              league ? team2Id : null,
            )
            return {
              key: item.match_id,
              item,
              action: candidate ? 'link' : 'create',
              team_id: teamId,
              team2_id: team2Id,
              target_id: candidate ? String(candidate.id) : '',
              imported: false,
            }
          }),
        )
      })
      .catch(() => setLoadErrors(['PlayHQ fixtures could not be loaded. Please try again.']))
  }, [scope, teamIds, league, clearSelection])

  useEffect(() => {
    load()
  }, [load])

  const updateRow = (key, changes) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...changes } : row)))

  // Applies a change to every ticked row; `changesFor` returns null where it does not apply.
  const applyBulk = (changesFor, what) => {
    let applied = 0
    let refused = 0
    const next = rows.map((row) => {
      if (!selection.selected.has(row.key)) return row
      const changes = changesFor(row)
      if (!changes) {
        refused++
        return row
      }
      applied++
      return { ...row, ...changes }
    })
    setRows(next)
    reportBulk(applied, refused, what)
  }

  const checked = useMemo(() => {
    const dupes = duplicateTargets(rows)
    return rows.map((row) => {
      let problem = null
      const noRound = `No round in your game covers ${row.item.start_date}. Import rounds on the Rounds tab (or add one by hand) first, or link it to an existing fixture.`
      if (row.action === 'create') {
        if (league && !row.team_id)
          problem = `Choose the home team (${row.item.team_name}). Linking teams on the Teams tab fills this in.`
        else if (league && !row.team2_id)
          problem = `Choose the away team (${row.item.opponent}). Linking teams on the Teams tab fills this in.`
        else if (league && row.team_id === row.team2_id)
          problem = 'The home and away teams cannot be the same.'
        else if (!row.team_id) problem = 'Choose which of your teams played this match.'
        else if (!row.item.round_id) problem = noRound
      } else if (row.action === 'link' || row.action === 'update') {
        if (!row.target_id) problem = 'Choose which of your fixtures this match is.'
        else if (dupes.has(String(row.target_id)))
          problem = 'Another match is linked to the same fixture.'
        else if (row.action === 'update' && !row.item.round_id) problem = noRound
      }
      return {
        ...row,
        problem,
        tone: row.action === 'skip' ? 'skipped' : problem ? 'attention' : 'ready',
      }
    })
  }, [rows, league])

  const countRows = (list) => {
    const c = {
      all: list.length,
      attention: 0,
      create: 0,
      link: 0,
      update: 0,
      skip: 0,
      imported: 0,
    }
    list.forEach((row) => {
      if (row.tone === 'attention') c.attention++
      if (row.imported) c.imported++
      c[row.action]++
    })
    return c
  }
  const counts = useMemo(() => countRows(checked), [checked])

  // Not linked / Already linked, then the filters. They only narrow what is shown: a ticked row
  // stays ticked when it is filtered out.
  const valueOf = useMemo(() => filterValue(league), [league])
  const viewCounts = useMemo(
    () => ({
      pending: checked.filter((row) => !row.imported).length,
      linked: checked.filter((row) => row.imported).length,
    }),
    [checked],
  )
  const shown = useMemo(
    () =>
      checked.filter(
        (row) => row.imported === (view === 'linked') && fitsFilters(row, filters, valueOf),
      ),
    [checked, view, filters, valueOf],
  )
  const shownCounts = useMemo(() => countRows(shown), [shown])
  const filtering = Object.values(filters).some((v) => (Array.isArray(v) ? v.length : v))

  const filterOptions = useMemo(() => {
    const byName = (a, b) => String(a.value).localeCompare(String(b.value))
    return {
      teams: optionsOf(
        checked.flatMap((row) => valueOf.teams(row)),
        (v) => v,
      ).sort(byName),
      grade: optionsOf(checked, valueOf.grade).sort(byName),
      round: optionsOf(checked, valueOf.round).sort(
        (a, b) => roundOrder(a.value) - roundOrder(b.value) || byName(a, b),
      ),
      ourRound: optionsOf(checked, valueOf.ourRound).sort((a, b) =>
        a.value === 'none' ? 1 : b.value === 'none' ? -1 : Number(a.value) - Number(b.value),
      ),
      status: optionsOf(checked, valueOf.status).sort(byName),
    }
    // Options only change when PlayHQ is read again, not when a row's action changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length, data, valueOf])

  const teamOptions = useMemo(
    () =>
      filterOptions.teams.map((o) => ({
        value: o.value,
        text: `${o.value} (${o.count})`,
        selected: filters.teams.includes(o.value),
      })),
    [filterOptions, filters.teams],
  )

  // What the import takes: the ticked rows when any are ticked, else every row shown that is
  // not skipped. Only those rows' problems hold it up - a future match with no round yet does
  // not stop the ones ticked from going in.
  const ticked = selection.selected.size > 0
  const toImport = useMemo(
    () =>
      (ticked ? checked : shown).filter(
        (row) => row.action !== 'skip' && (!ticked || selection.selected.has(row.key)),
      ),
    [checked, shown, ticked, selection.selected],
  )
  const hiddenTicked = ticked
    ? [...selection.selected].filter((key) => !shown.some((row) => row.key === key)).length
    : 0
  const importCounts = useMemo(() => {
    const c = { attention: 0, noRound: 0, create: 0, link: 0, update: 0 }
    toImport.forEach((row) => {
      if (row.tone === 'attention') c.attention++
      // Creating or updating a fixture needs one of our rounds to cover its start.
      if (row.action !== 'link' && !row.item.round_id) c.noRound++
      c[row.action]++
    })
    return c
  }, [toImport])

  const runImport = async () => {
    setImporting(true)
    setImportErrors([])
    try {
      const payload = toImport.map((row) => ({
        match_id: row.item.match_id,
        action: row.action,
        team_id: row.team_id || null,
        team2_id: league ? row.team2_id || null : null,
        fixture_id: row.target_id || null,
      }))
      const res = await PlayHQService.runImport('fixtures', { rows: payload })
      if (res.success) {
        ToastComponent(res.message || 'Fixtures imported.', 'success')
        setResult(res.message)
        if (onImported) onImported()
      } else {
        setImportErrors(errorsOf(res))
      }
    } catch (e) {
      setImportErrors(['The import could not be run. Please try again.'])
    } finally {
      setImporting(false)
    }
  }

  if (loadErrors.length) {
    return (
      <>
        <ImportErrorAlert errors={loadErrors} title="PlayHQ data could not be loaded:" />
        <CButton color="primary" variant="outline" onClick={load}>
          Try again
        </CButton>
      </>
    )
  }
  if (!data) return <LoadingBlock text="Loading matches from PlayHQ…" />
  if (result) {
    return (
      <>
        <InfoBox color="success">{result}</InfoBox>
        <CButton color="primary" variant="outline" onClick={load}>
          Review fixtures again
        </CButton>
      </>
    )
  }
  if (!rows.length) return <InfoBox color="light">PlayHQ has no matches here.</InfoBox>

  const ourFixtures = data.our_fixtures || []
  const ourTeams = data.our_teams || []
  const fixtureById = (id) => ourFixtures.find((f) => String(f.id) === String(id))
  const teamById = (id) => ourTeams.find((t) => String(t.id) === String(id))

  const visible = shown.filter((row) => {
    if (filter === 'attention') return row.tone === 'attention'
    if (filter === 'create') return row.action === 'create'
    if (filter === 'existing') return row.action === 'link' || row.action === 'update'
    if (filter === 'skip') return row.action === 'skip'
    return true
  })

  const matchCell = (row) => (
    <>
      <div className="fw-semibold">
        {row.item.round_short || row.item.round_name} · {row.item.team_name} vs {row.item.opponent}
      </div>
      <div className="small text-medium-emphasis">
        {formatDate(row.item.start_date, row.item.start_time)}
        {row.item.end_date !== row.item.start_date && ` → ${formatDate(row.item.end_date)}`}
      </div>
      <div className="small text-medium-emphasis">{row.item.venue}</div>
      <div className="d-flex gap-1 mt-1 flex-wrap">
        <CBadge color={STATUS_COLOR[row.item.status] || 'secondary'} className="fw-normal">
          {statusLabel(row.item.status)}
        </CBadge>
        <CBadge color="light" textColor="dark" className="fw-normal">
          {row.item.match_type_name}
        </CBadge>
        {league && row.item.grade_name && (
          <CBadge color="light" textColor="dark" className="fw-normal">
            {row.item.grade_name}
          </CBadge>
        )}
        {row.imported && (
          <CBadge color="success" shape="rounded-pill" className="fw-normal">
            Already imported
          </CBadge>
        )}
        {row.item.round_number ? (
          <CBadge color="light" textColor="dark" className="fw-normal">
            Your round {row.item.round_number}
          </CBadge>
        ) : (
          <CBadge color="warning" className="fw-normal">
            No round
          </CBadge>
        )}
      </div>
      {row.problem && <div className="small text-warning mt-1">{row.problem}</div>}
    </>
  )

  const filterSelect = (key, label, allLabel, describe = (v) => v) => (
    <CCol xs={12} sm={6} lg>
      <CFormLabel className="small fw-semibold mb-1">{label}</CFormLabel>
      <CFormSelect
        size="sm"
        value={filters[key]}
        onChange={(e) => setFilters((f) => ({ ...f, [key]: e.target.value }))}
      >
        <option value="">{allLabel}</option>
        {filterOptions[key].map((o) => (
          <option key={o.value} value={o.value}>
            {describe(o.value)} ({o.count})
          </option>
        ))}
      </CFormSelect>
    </CCol>
  )

  const renderFilters = () => (
    <div className="border rounded-4 p-3 mb-3" style={tableHeadStyle}>
      <CRow className="g-2 align-items-end">
        <CCol xs={12} lg={3}>
          <CFormLabel className="small fw-semibold mb-1">Teams</CFormLabel>
          <CMultiSelect
            size="sm"
            options={teamOptions}
            selectionType="tags"
            placeholder="All teams"
            searchNoResultsLabel="No teams found"
            onChange={(picked) => {
              const values = (Array.isArray(picked) ? picked : []).map((o) => o.value)
              // The select reports its selection again whenever its options change; only a
              // real change is kept, or the two would keep updating each other.
              if (!sameValues(values, filters.teams)) setFilters((f) => ({ ...f, teams: values }))
            }}
          />
        </CCol>
        {filterSelect('grade', 'Grade', 'All grades')}
        {filterSelect('round', 'PlayHQ round', 'All PlayHQ rounds')}
        {filterSelect('ourRound', 'Your round', 'All your rounds', (v) =>
          v === 'none' ? 'No round' : `Round ${v}`,
        )}
        {filterSelect('status', 'Match status', 'Any status', statusLabel)}
        <CCol xs="auto">
          <CButton
            size="sm"
            color="secondary"
            variant="ghost"
            disabled={!filtering}
            onClick={() => setFilters(NO_FILTERS)}
          >
            Clear filters
          </CButton>
        </CCol>
      </CRow>
      <div className="small text-medium-emphasis mt-2">
        Showing {shown.length} of {viewCounts[view]} matches. PlayHQ round numbers differ between
        grades - pick a team too, or use Your round to get one weekend across every team.
      </div>
    </div>
  )

  const renderValidate = () => (
    <>
      <InfoBox color="light">
        Create each PlayHQ match as a fixture, or link it to a fixture you already made. Link keeps
        your fixture as it is; Link &amp; update takes PlayHQ&rsquo;s dates, venue
        {league ? '' : ' and opponent'}. A fixture&rsquo;s round comes from its start date, as when
        you add one by hand.
        {league &&
          ' Each match is one fixture, home team v away team - both teams are filled in from the teams you linked.'}
      </InfoBox>
      <div className="d-flex gap-3 flex-wrap mb-3">
        <StatTile
          label="Ready"
          value={counts.all - counts.attention - counts.skip}
          tone="success"
          icon={cilCheckCircle}
        />
        <StatTile
          label="Needs attention"
          value={counts.attention}
          tone="warning"
          icon={cilWarning}
        />
        <StatTile label="Already imported" value={counts.imported} tone="info" icon={cilLink} />
      </div>
      <LinkedViewTabs value={view} onChange={setView} counts={viewCounts} noun="matches" />
      {renderFilters()}
      <FilterPills
        value={filter}
        onChange={setFilter}
        filters={[
          { key: 'all', label: 'All', count: shownCounts.all },
          { key: 'attention', label: 'Needs attention', count: shownCounts.attention },
          { key: 'create', label: 'Create new', count: shownCounts.create },
          {
            key: 'existing',
            label: 'Link to existing',
            count: shownCounts.link + shownCounts.update,
          },
          { key: 'skip', label: 'Skipped', count: shownCounts.skip },
        ]}
      />
      <BulkBar count={selection.selected.size} onClear={selection.clear}>
        <BulkSelect
          placeholder="Set action…"
          options={Object.entries(ACTIONS).map(([value, a]) => ({ value, label: a.label }))}
          onPick={(action) =>
            applyBulk(
              (row) => (allowedActions(row).includes(action) ? { action } : null),
              `"${ACTIONS[action].label}"`,
            )
          }
        />
        {!league && (
          <BulkSelect
            placeholder="Set team (new fixtures)…"
            options={ourTeams.map((t) => ({ value: String(t.id), label: teamLabel(t) }))}
            onPick={(team_id) =>
              applyBulk((row) => (row.action === 'create' ? { team_id } : null), 'Team')
            }
          />
        )}
      </BulkBar>
      {!visible.length ? (
        <InfoBox color="light">
          {!viewCounts[view]
            ? view === 'linked'
              ? 'No PlayHQ matches imported yet.'
              : 'Every PlayHQ match here is already imported.'
            : 'No matches fit these filters.'}
        </InfoBox>
      ) : (
        <CCard className="border-secondary-subtle rounded-4 overflow-hidden mb-3">
          <div className="table-responsive">
            <CTable align="middle" hover className="mb-0">
              <CTableHead className="align-middle" style={tableHeadStyle}>
                <CTableRow>
                  <CTableHeaderCell className="ps-3" style={CHECK_CELL_STYLE}>
                    <SelectAllCheckbox keys={visible.map((row) => row.key)} selection={selection} />
                  </CTableHeaderCell>
                  <CTableHeaderCell>PlayHQ match</CTableHeaderCell>
                  <CTableHeaderCell>Action</CTableHeaderCell>
                  <CTableHeaderCell>
                    {league ? 'Home / away teams or fixture' : 'Your team / fixture'}
                  </CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {visible.map((row) => (
                  <CTableRow key={row.key} style={rowStyle(row.tone)}>
                    <CTableDataCell className="ps-3">
                      <RowCheckbox
                        checked={selection.selected.has(row.key)}
                        onChange={() => selection.toggle(row.key)}
                        title={`Select ${row.item.round_short} ${row.item.team_name} vs ${row.item.opponent}`}
                      />
                    </CTableDataCell>
                    <CTableDataCell>{matchCell(row)}</CTableDataCell>
                    <CTableDataCell>
                      <ActionSelect
                        value={row.action}
                        onChange={(action) => updateRow(row.key, { action })}
                        allowed={allowedActions(row)}
                      />
                    </CTableDataCell>
                    <CTableDataCell>
                      {row.action === 'create' && !league && (
                        <RecordSelect
                          value={row.team_id}
                          onChange={(team_id) => updateRow(row.key, { team_id })}
                          records={ourTeams}
                          getLabel={teamLabel}
                          placeholder="Choose team…"
                          invalid={!row.team_id}
                        />
                      )}
                      {row.action === 'create' && league && (
                        <div className="d-flex flex-column gap-1">
                          <RecordSelect
                            value={row.team_id}
                            onChange={(team_id) => updateRow(row.key, { team_id })}
                            records={ourTeams}
                            getLabel={teamLabel}
                            placeholder="Choose home team…"
                            invalid={!row.team_id}
                          />
                          <RecordSelect
                            value={row.team2_id}
                            onChange={(team2_id) => updateRow(row.key, { team2_id })}
                            records={ourTeams}
                            getLabel={teamLabel}
                            placeholder="Choose away team…"
                            invalid={!row.team2_id || row.team2_id === row.team_id}
                          />
                        </div>
                      )}
                      {(row.action === 'link' || row.action === 'update') &&
                        (row.imported ? (
                          <span className="small">
                            {fixtureById(row.target_id)
                              ? fixtureLabel(fixtureById(row.target_id))
                              : '—'}
                          </span>
                        ) : (
                          <FixtureSelect
                            value={row.target_id}
                            onChange={(target_id) => updateRow(row.key, { target_id })}
                            fixtures={ourFixtures.filter(
                              (f) => !f.playhq_id || String(f.id) === String(row.target_id),
                            )}
                            roundId={row.item.round_id}
                            placeholder="Choose fixture…"
                            invalid={!row.target_id}
                          />
                        ))}
                      {row.action === 'skip' && (
                        <span className="small text-medium-emphasis">
                          {row.imported && fixtureById(row.target_id)
                            ? fixtureLabel(fixtureById(row.target_id))
                            : '—'}
                        </span>
                      )}
                    </CTableDataCell>
                  </CTableRow>
                ))}
              </CTableBody>
            </CTable>
          </div>
        </CCard>
      )}
      <div className="d-flex justify-content-end align-items-center gap-2 flex-wrap">
        {importCounts.attention > 0 ? (
          <span className="small text-warning me-auto">
            {importCounts.attention} {ticked ? 'ticked ' : ''}
            {importCounts.attention === 1 ? 'match needs' : 'matches need'} attention
            {importCounts.noRound > 0 &&
              ` - ${importCounts.noRound} ${
                importCounts.noRound === 1 ? 'has' : 'have'
              } no round in your game covering ${
                importCounts.noRound === 1 ? 'its date' : 'their dates'
              }, and a fixture cannot be saved without one. Import rounds on the Rounds tab (or check the season is right) first`}
            . Fix {importCounts.attention === 1 ? 'it' : 'them'}, set{' '}
            {importCounts.attention === 1 ? 'it' : 'them'} to Skip
            {ticked ? ', or untick' : ', or tick only the matches you want to import'}.
          </span>
        ) : (
          toImport.length > 0 &&
          (ticked || toImport.length < counts.all - counts.skip) && (
            <span className="small text-medium-emphasis me-auto">
              Only the {toImport.length} {ticked ? 'ticked' : 'shown'}{' '}
              {toImport.length === 1 ? 'match' : 'matches'} will be imported
              {hiddenTicked > 0 &&
                ` - ${hiddenTicked} of them ${hiddenTicked === 1 ? 'is' : 'are'} not shown here`}
              .
            </span>
          )
        )}
        <CButton color="secondary" variant="outline" onClick={load}>
          Reload from PlayHQ
        </CButton>
        <CButton
          color="primary"
          disabled={importCounts.attention > 0 || !toImport.length}
          onClick={() => setStep(2)}
        >
          Review {toImport.length} {toImport.length === 1 ? 'fixture' : 'fixtures'}
        </CButton>
      </div>
    </>
  )

  const renderReview = () => {
    return (
      <>
        <div className="d-flex gap-3 flex-wrap mb-3">
          <StatTile label="Create new" value={importCounts.create} tone="success" icon={cilPlus} />
          <StatTile label="Link to existing" value={importCounts.link} tone="info" icon={cilLink} />
          <StatTile
            label="Update from PlayHQ"
            value={importCounts.update}
            tone="warning"
            icon={cilSync}
          />
          <StatTile
            label={ticked ? 'Not imported' : 'Skipped'}
            value={counts.all - toImport.length}
            tone="secondary"
            icon={cilX}
          />
        </div>
        <CCard className="border-secondary-subtle rounded-4 overflow-hidden mb-3">
          <div className="table-responsive">
            <CTable align="middle" className="mb-0">
              <CTableHead className="align-middle" style={tableHeadStyle}>
                <CTableRow>
                  <CTableHeaderCell className="ps-3">PlayHQ match</CTableHeaderCell>
                  <CTableHeaderCell>What happens</CTableHeaderCell>
                  <CTableHeaderCell>Your fixture</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {toImport.map((row) => (
                  <CTableRow key={row.key}>
                    <CTableDataCell className="ps-3">
                      {row.item.round_short} · {row.item.team_name} vs {row.item.opponent}
                      <div className="small text-medium-emphasis">
                        {formatDate(row.item.start_date, row.item.start_time)}
                      </div>
                    </CTableDataCell>
                    <CTableDataCell>{ACTIONS[row.action].label}</CTableDataCell>
                    <CTableDataCell>
                      {row.action === 'create'
                        ? `New: ${teamById(row.team_id)?.name || ''} vs ${
                            league ? teamById(row.team2_id)?.name || '' : row.item.opponent
                          } · ${row.item.match_type_name} · your round ${row.item.round_number}`
                        : fixtureById(row.target_id)
                        ? fixtureLabel(fixtureById(row.target_id))
                        : '—'}
                    </CTableDataCell>
                  </CTableRow>
                ))}
              </CTableBody>
            </CTable>
          </div>
        </CCard>
        <ImportErrorAlert errors={importErrors} />
        <div className="d-flex justify-content-end gap-2">
          <CButton color="secondary" variant="outline" onClick={() => setStep(1)}>
            Back
          </CButton>
          <CLoadingButton color="primary" loading={importing} onClick={runImport}>
            Import {toImport.length} {toImport.length === 1 ? 'fixture' : 'fixtures'}
          </CLoadingButton>
        </div>
      </>
    )
  }

  return (
    <>
      <MiniStepper steps={STEPS} current={step} maxReached={step} onStepClick={setStep} />
      {step === 1 ? renderValidate() : renderReview()}
    </>
  )
}

export default FixturesImporter
